import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const Winkel = () => {
  const { t, pick } = useLang();
  const SORTS = [
    ["default", t("shop.sortDefault")],
    ["price_asc", t("shop.sortPriceAsc")],
    ["price_desc", t("shop.sortPriceDesc")],
    ["rating", t("shop.sortRating")],
    ["name", t("shop.sortName")],
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const brand = searchParams.get("brand") || "";
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");
  const [inStock, setInStock] = useState(false);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brands").then((d) => setBrands(d.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { q, brand, sort, in_stock: inStock || undefined } })
      .then((d) => setProducts(d.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [q, brand, sort, inStock]);

  const pickBrand = (slug) => {
    setSearchParams(slug ? { brand: slug } : {});
  };

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader
        eyebrow={t("shop.eyebrow")}
        title={<>{t("shop.title1")} <span className="italic normal-case text-gold">{t("shop.title2")}</span></>}
        sub={t("shop.sub")}
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-smoke" />
          <input
            data-testid="product-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("shop.search")}
            className="w-full bg-ink border border-gold/15 pl-11 pr-4 py-3.5 text-sm text-cream placeholder:text-smoke focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>
        <div className="flex items-center gap-4">
          <select
            data-testid="product-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-ink border border-gold/15 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-gold/50"
          >
            {SORTS.map(([v, label]) => (
              <option key={v} value={v} className="bg-ink">{label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              data-testid="in-stock-toggle"
              type="checkbox"
              checked={inStock}
              onChange={(e) => setInStock(e.target.checked)}
              className="h-4 w-4 accent-gold"
            />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-mist">{t("shop.inStock")}</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-3 -mx-6 px-6 sm:mx-0 sm:px-0 mb-10">
        <button
          data-testid="brand-filter-all"
          onClick={() => pickBrand("")}
          className={`shrink-0 px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase border transition-all duration-300 ${
            !brand ? "border-gold bg-gold/10 text-gold" : "border-cream/10 text-mist hover:border-gold/40 hover:text-cream"
          }`}
        >
          {t("shop.all")}
        </button>
        {brands.map((b) => (
          <button
            key={b.slug}
            data-testid={`brand-filter-${b.slug}`}
            onClick={() => pickBrand(b.slug)}
            className={`shrink-0 px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase border transition-all duration-300 ${
              brand === b.slug ? "border-gold bg-gold/10 text-gold" : "border-cream/10 text-mist hover:border-gold/40 hover:text-cream"
            }`}
          >
            {pick(b, "name")}
            {b.product_count > 0 && <span className="ml-2 text-gold/60">{b.product_count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-ink animate-pulse border border-gold/5" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div data-testid="shop-empty-state" className="border border-gold/15 bg-ink px-8 py-20 text-center">
          <p className="font-serif text-2xl text-cream">{t("shop.empty")}</p>
          <p className="mt-3 text-sm text-mist">{t("shop.emptySub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Winkel;
